import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ClipboardList,
  PackageSearch,
  ArrowLeft,
  Store as StoreIcon,
  RefreshCw,
  User,
  Settings,
  LogOut,
  LayoutDashboard,
  TrendingUp,
} from 'lucide-react';
import {
  Order,
  Product,
  Store,
  OrderSheet,
  UserProfile,
  OrderStatus,
  SellerOrderSortBy,
} from '../../types';
import { StoresTab } from './StoresTab';
import { ProductsTab } from './ProductsTab';
import { OrdersTab } from './OrdersTab/OrdersTab';
import { CustomersTab } from './CustomersTab';
import { SettingsTab } from './SettingsTab';
import { ProductEditor } from './ProductEditor';
import { DashboardTab } from './DashboardTab';
import { AdminOrderModal } from './AdminOrderModal';
import { CustomerOrderHistoryModal } from './CustomerOrderHistoryModal';
import { HistoryTab } from './HistoryTab';
import { History } from 'lucide-react';
import {
  getSellerCustomersPage,
  getSellerOrderMetaPage,
  getSellerOrderProductsPage,
  getSellerOrdersPage,
  getSellerProductMetaPage,
  getSellerProductMetricsPage,
  getSellerProductsPage,
  getSellerStoreMetricsPage,
  getSellerStoresPage,
  SellerCustomersPageData,
  SellerOrderMetaPageData,
  SellerOrderProductsPageData,
  SellerOrdersPageData,
  SellerProductMetaPageData,
  SellerProductMetricsPageData,
  SellerProductSortBy,
  SellerProductsPageData,
  SellerStoreMetricsPageData,
  SellerStoresPageData,
} from '../../api/seller-pages';
import { createProduct, deleteProduct } from '../../api/products';
import { updateProductWithStockConfirmation } from '../../utils/productStockReduction';
import { settleOrderSheet } from '../../api/order-sheets';
import { updateCustomerAlias } from '../../api/customers';
import { updateOrderStatus, updateOrderStatusBatch } from '../../api/orders';

type ActiveTab =
  | 'DASHBOARD'
  | 'PRODUCTS'
  | 'ORDERS'
  | 'STORES'
  | 'INVENTORY'
  | 'CUSTOMERS'
  | 'SETTINGS'
  | 'LOGS';

interface SellerDashboardProps {
  onExit: () => void;
  onLogout?: () => void;
  user?: UserProfile & { id?: string };
}

const TAB_ITEMS = [
  { id: 'DASHBOARD', icon: LayoutDashboard, label: '대시보드' },
  { id: 'STORES', icon: StoreIcon, label: '배송지 관리' },
  { id: 'PRODUCTS', icon: PackageSearch, label: '상품 관리' },
  { id: 'ORDERS', icon: ClipboardList, label: '주문 관리' },
  { id: 'CUSTOMERS', icon: User, label: '고객 관리' },
  { id: 'LOGS', icon: History, label: '이력 관리' },
] as const;

const VISIBLE_TAB_ITEMS = TAB_ITEMS;

const EMPTY_ORDERS: Order[] = [];
const EMPTY_PRODUCTS: Product[] = [];
const EMPTY_STORES: Store[] = [];
const EMPTY_ORDER_SHEETS: OrderSheet[] = [];
const EMPTY_PRODUCT_METRICS_PAGE: SellerProductMetricsPageData = {
  activeProductQuantities: {},
  activeOptionQuantities: {},
  orderMetricsBySheet: {},
};
const PRODUCTS_PAGE_SIZE = 20;
const ORDERS_PAGE_SIZE = 20;
const ORDER_SELECTION_PAGE_SIZE = 100;
const SELLER_TAB_PATHS: Record<ActiveTab, string> = {
  DASHBOARD: '/backstage/dashboard',
  STORES: '/backstage/stores',
  PRODUCTS: '/backstage/products',
  ORDERS: '/backstage/orders',
  INVENTORY: '/backstage/inventory',
  CUSTOMERS: '/backstage/customers',
  SETTINGS: '/backstage/settings',
  LOGS: '/backstage/logs',
};
const SELLER_TAB_LABELS: Record<ActiveTab, string> = {
  DASHBOARD: '대시보드',
  STORES: '배송지 관리',
  PRODUCTS: '상품 관리',
  ORDERS: '주문 관리',
  INVENTORY: '재고 관리',
  CUSTOMERS: '고객 관리',
  SETTINGS: '환경설정',
  LOGS: '이력 관리',
};
const SELLER_PATH_TAB_MAP: Record<string, ActiveTab> = {
  dashboard: 'DASHBOARD',
  stores: 'STORES',
  products: 'PRODUCTS',
  orders: 'ORDERS',
  inventory: 'INVENTORY',
  customers: 'CUSTOMERS',
  settings: 'SETTINGS',
  logs: 'LOGS',
};
const REMOVED_TAB_PATHS = ['/backstage/weekly-report', '/backstage/events'];

const getSellerTabFromPath = (path: string): ActiveTab => {
  const normalizedPath = path.replace(/\/+$/, '') || '/';

  if (normalizedPath === '/backstage') {
    return 'DASHBOARD';
  }

  if (!normalizedPath.startsWith('/backstage/')) {
    return 'DASHBOARD';
  }

  const slug = normalizedPath.slice('/backstage/'.length).split('/')[0];

  const nextTab = SELLER_PATH_TAB_MAP[slug] || 'DASHBOARD';

  return nextTab;
};

const getSellerPathForTab = (tab: ActiveTab) =>
  SELLER_TAB_PATHS[tab] || '/backstage/dashboard';

const getLatestOrderSheetIdsByStore = (orderSheets: OrderSheet[]) => {
  const latestSheetByStore = new Map<string, string>();

  [...orderSheets]
    .sort(
      (a, b) =>
        new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime(),
    )
    .forEach((sheet) => {
      if (!latestSheetByStore.has(sheet.storeId)) {
        latestSheetByStore.set(sheet.storeId, sheet.id);
      }
    });

  return Array.from(latestSheetByStore.values());
};

const mergeProductsWithMetrics = (
  products: Product[],
  metricsPage?: SellerProductMetricsPageData | null,
) => {
  const activeProductQuantities =
    metricsPage?.activeProductQuantities ||
    EMPTY_PRODUCT_METRICS_PAGE.activeProductQuantities;
  const activeOptionQuantities =
    metricsPage?.activeOptionQuantities ||
    EMPTY_PRODUCT_METRICS_PAGE.activeOptionQuantities;

  return products.map((product) => {
    const options =
      product.options?.map((option) => ({
        ...option,
        availableStock: Math.max(
          0,
          Number(option.stock || 0) - (activeOptionQuantities[option.id] || 0),
        ),
      })) || [];

    const totalStock =
      options.length > 0
        ? options.reduce((sum, option) => sum + Number(option.stock || 0), 0)
        : product.totalStock || 0;
    const availableStock =
      options.length > 0
        ? options.reduce(
            (sum, option) => sum + Number(option.availableStock || 0),
            0,
          )
        : Math.max(
            0,
            Number(product.totalStock || 0) -
              (activeProductQuantities[product.id] || 0),
          );

    return {
      ...product,
      totalStock,
      availableStock,
      options,
    };
  });
};

export const SellerDashboard: React.FC<SellerDashboardProps> = ({
  onExit,
  onLogout,
  user,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>(() =>
    getSellerTabFromPath(window.location.pathname),
  );
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [debouncedProductSearchTerm, setDebouncedProductSearchTerm] =
    useState('');
  const [productActiveTag, setProductActiveTag] = useState<string | null>(null);
  const [productSortBy, setProductSortBy] =
    useState<SellerProductSortBy>('DEFAULT');
  const [productSelectedOrderSheetIds, setProductSelectedOrderSheetIds] =
    useState<string[]>(['all']);
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [orderSelectedOrderSheetIds, setOrderSelectedOrderSheetIds] = useState<
    string[]
  >(['all']);
  const [orderCurrentPage, setOrderCurrentPage] = useState(1);
  const [orderSelectedStoreId, setOrderSelectedStoreId] = useState<
    string | undefined
  >(undefined);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(
    null,
  );
  const [isAdminOrderModalOpen, setIsAdminOrderModalOpen] = useState(false);
  const [adminOrderInitialCustomer, setAdminOrderInitialCustomer] = useState({
    name: '',
    phone: '',
    userId: '',
  });
  const [editingOrder, setEditingOrder] = useState<Order | undefined>(undefined);
  const [historyCustomer, setHistoryCustomer] = useState<{
    name: string;
    phone: string;
    userId?: string;
    orderSheetId?: string;
  } | null>(null);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [debouncedOrderSearchTerm, setDebouncedOrderSearchTerm] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState<'ALL' | OrderStatus>(
    'ALL',
  );
  const [orderSortBy, setOrderSortBy] = useState<SellerOrderSortBy>('name');
  const [adminOrderInitialStoreId, setAdminOrderInitialStoreId] = useState('');
  const [adminOrderInitialOrderSheetId, setAdminOrderInitialOrderSheetId] =
    useState('');
  const [storesPage, setStoresPage] = useState<SellerStoresPageData | null>(
    null,
  );
  const [storeMetricsPage, setStoreMetricsPage] =
    useState<SellerStoreMetricsPageData | null>(null);
  const [productMetaPage, setProductMetaPage] =
    useState<SellerProductMetaPageData | null>(null);
  const [productsPage, setProductsPage] =
    useState<SellerProductsPageData | null>(null);
  const [productMetricsPage, setProductMetricsPage] =
    useState<SellerProductMetricsPageData | null>(null);
  const productPagePromiseRef =
    useRef<Promise<SellerProductsPageData | null> | null>(null);
  const productPagePromiseKeyRef = useRef('');
  const [orderMetaPage, setOrderMetaPage] =
    useState<SellerOrderMetaPageData | null>(null);
  const [orderProductsPage, setOrderProductsPage] =
    useState<SellerOrderProductsPageData | null>(null);
  const [ordersPage, setOrdersPage] = useState<SellerOrdersPageData | null>(
    null,
  );
  const [isOrdersBundleLoading, setIsOrdersBundleLoading] = useState(false);
  const ordersBundlePromiseRef = useRef<Promise<unknown> | null>(null);
  const [customersPage, setCustomersPage] =
    useState<SellerCustomersPageData | null>(null);
  const [dashboardRefreshToken, setDashboardRefreshToken] = useState(0);
  const [loadingTab, setLoadingTab] = useState<ActiveTab | null>(null);
  const [tabError, setTabError] = useState<string | null>(null);
  const lastLoadedProductFilterKeyRef = useRef('');
  const lastLoadedOrderFilterKeyRef = useRef('');

  const navigateToTab = useCallback(
    (tab: ActiveTab, options?: { replace?: boolean }) => {
      const nextPath = getSellerPathForTab(tab);

      setActiveTab(tab);

      if (window.location.pathname === nextPath) {
        return;
      }

      if (options?.replace) {
        window.history.replaceState({}, '', nextPath);
        return;
      }

      window.history.pushState({}, '', nextPath);
    },
    [],
  );

  useEffect(() => {
    const normalizedPath = window.location.pathname.replace(/\/+$/, '');

    if (REMOVED_TAB_PATHS.some((path) => normalizedPath === path || normalizedPath.startsWith(`${path}/`))) {
      navigateToTab('DASHBOARD', { replace: true });
    }
  }, [navigateToTab]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedProductSearchTerm(productSearchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [productSearchTerm]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedOrderSearchTerm(orderSearchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [orderSearchTerm]);

  useEffect(() => {
    const syncTabWithPath = () => {
      const nextTab = getSellerTabFromPath(window.location.pathname);

      setActiveTab((current) => (current === nextTab ? current : nextTab));
    };

    window.addEventListener('popstate', syncTabWithPath);

    return () => {
      window.removeEventListener('popstate', syncTabWithPath);
    };
  }, []);

  const invalidateTabs = useCallback((tabs: ActiveTab[]) => {
    tabs.forEach((tab) => {
      if (tab === 'DASHBOARD') {
        setDashboardRefreshToken((current) => current + 1);
      }
      if (tab === 'STORES') {
        setStoresPage(null);
      }
      if (tab === 'PRODUCTS') {
        setProductsPage(null);
      }
      if (tab === 'ORDERS') {
        setOrdersPage(null);
      }
      if (tab === 'CUSTOMERS') {
        setCustomersPage(null);
      }
    });
  }, []);

  const invalidateStoreMetrics = useCallback(() => {
    setStoreMetricsPage(null);
  }, []);

  const invalidateProductMeta = useCallback(() => {
    setProductMetaPage(null);
  }, []);

  const invalidateProductMetrics = useCallback(() => {
    setProductMetricsPage(null);
  }, []);

  const invalidateOrderMeta = useCallback(() => {
    setOrderMetaPage(null);
  }, []);

  const invalidateOrderProducts = useCallback(() => {
    setOrderProductsPage(null);
  }, []);

  const invalidateOrderRelatedTabsExceptOrders = useCallback(() => {
    const tabsToInvalidate: ActiveTab[] = ['DASHBOARD', 'CUSTOMERS'];

    if (
      productSortBy !== 'DEFAULT' ||
      !productSelectedOrderSheetIds.includes('all')
    ) {
      tabsToInvalidate.push('PRODUCTS');
    }

    invalidateTabs(tabsToInvalidate);
    invalidateProductMetrics();
    invalidateStoreMetrics();
  }, [
    invalidateProductMetrics,
    invalidateStoreMetrics,
    invalidateTabs,
    productSelectedOrderSheetIds,
    productSortBy,
  ]);

  const getProductOrderSheetRequestIds = useCallback((ids: string[]) => {
    return ids.includes('all') ? undefined : ids;
  }, []);

  const getProductMetricOrderSheetRequestIds = useCallback(
    (ids: string[], orderSheets?: OrderSheet[]) => {
      if (!ids.includes('all')) {
        return ids;
      }

      const latestOrderSheetIds = getLatestOrderSheetIdsByStore(
        orderSheets || [],
      );

      return latestOrderSheetIds.length > 0 ? latestOrderSheetIds : undefined;
    },
    [],
  );

  const getProductFilterKey = useCallback(
    (params?: {
      searchTerm?: string;
      activeTag?: string | null;
      sortBy?: SellerProductSortBy;
      selectedOrderSheetIds?: string[];
      page?: number;
    }) => {
      const selectedOrderSheetIds =
        params?.selectedOrderSheetIds || productSelectedOrderSheetIds;
      const normalizedSheetKey = selectedOrderSheetIds.includes('all')
        ? 'all'
        : [...selectedOrderSheetIds].sort().join(',');

      return [
        params?.searchTerm !== undefined
          ? params.searchTerm
          : debouncedProductSearchTerm,
        params?.activeTag !== undefined
          ? params.activeTag || 'ALL'
          : productActiveTag || 'ALL',
        params?.sortBy !== undefined ? params.sortBy : productSortBy,
        params?.page !== undefined ? params.page : productCurrentPage,
        normalizedSheetKey,
      ].join('::');
    },
    [
      debouncedProductSearchTerm,
      productActiveTag,
      productCurrentPage,
      productSelectedOrderSheetIds,
      productSortBy,
    ],
  );

  const getOrderFilterKey = useCallback(
    (params?: {
      selectedOrderSheetIds?: string[];
      searchTerm?: string;
      statusFilter?: 'ALL' | OrderStatus;
      storeId?: string;
      sortBy?: SellerOrderSortBy;
      page?: number;
    }) => {
      const selectedOrderSheetIds =
        params?.selectedOrderSheetIds || orderSelectedOrderSheetIds;
      const normalizedSheetKey = selectedOrderSheetIds.includes('all')
        ? 'all'
        : [...selectedOrderSheetIds].sort().join(',');

      return [
        normalizedSheetKey,
        params?.searchTerm !== undefined
          ? params.searchTerm
          : debouncedOrderSearchTerm,
        params?.statusFilter !== undefined
          ? params.statusFilter
          : orderStatusFilter,
        params?.storeId !== undefined
          ? params.storeId || 'ALL'
          : orderSelectedStoreId || 'ALL',
        params?.sortBy !== undefined ? params.sortBy : orderSortBy,
        params?.page !== undefined ? params.page : orderCurrentPage,
      ].join('::');
    },
    [
      debouncedOrderSearchTerm,
      orderCurrentPage,
      orderSelectedOrderSheetIds,
      orderSelectedStoreId,
      orderSortBy,
      orderStatusFilter,
    ],
  );

  const doesOrderMatchCurrentList = useCallback(
    (order: Order) => {
      const scopedOrderSheetIds = orderSelectedOrderSheetIds.includes('all')
        ? (() => {
            const latestSheetByStore = new Map<string, string>();

            [...(orderMetaPage?.orderSheets || EMPTY_ORDER_SHEETS)]
              .sort(
                (a, b) =>
                  new Date(b.deliveryDate).getTime() -
                  new Date(a.deliveryDate).getTime(),
              )
              .forEach((sheet) => {
                if (!latestSheetByStore.has(sheet.storeId)) {
                  latestSheetByStore.set(sheet.storeId, sheet.id);
                }
              });

            return Array.from(latestSheetByStore.values());
          })()
        : orderSelectedOrderSheetIds;
      const normalizedSearchTerm = debouncedOrderSearchTerm.toLowerCase();
      const matchesSearch =
        normalizedSearchTerm.length === 0 ||
        `${order.customerName}${order.customerPhone || ''}`
          .toLowerCase()
          .includes(normalizedSearchTerm);
      const matchesSheet = scopedOrderSheetIds.includes(order.orderSheetId || '');
      const matchesStatus =
        orderStatusFilter === 'ALL' || order.status === orderStatusFilter;
      const matchesStore =
        !orderSelectedStoreId || order.storeId === orderSelectedStoreId;

      return matchesSearch && matchesSheet && matchesStatus && matchesStore;
    },
    [
      debouncedOrderSearchTerm,
      orderMetaPage?.orderSheets,
      orderSelectedOrderSheetIds,
      orderSelectedStoreId,
      orderStatusFilter,
    ],
  );

  const upsertOrderInOrdersPage = useCallback((order: Order) => {
    setOrdersPage((current) => {
      if (!current) {
        return current;
      }

      const hasGroupedOrders = current.orders.some(
        (candidate) =>
          Array.isArray(candidate.aggregatedIds) &&
          candidate.aggregatedIds.length > 0,
      );

      if (hasGroupedOrders) {
        return current;
      }

      const existingIndex = current.orders.findIndex(
        (candidate) => candidate.id === order.id,
      );
      const matchesCurrentList = doesOrderMatchCurrentList(order);

      if (!matchesCurrentList) {
        if (existingIndex === -1) {
          return current;
        }

        return {
          ...current,
          orders: current.orders.filter((candidate) => candidate.id !== order.id),
        };
      }

      if (existingIndex === -1) {
        return {
          ...current,
          orders: [order, ...current.orders],
        };
      }

      return {
        ...current,
        orders: current.orders.map((candidate) =>
          candidate.id === order.id ? { ...candidate, ...order } : candidate,
        ),
      };
    });
  }, [doesOrderMatchCurrentList]);

  const removeOrderFromOrdersPage = useCallback((orderId: string) => {
    setOrdersPage((current) => {
      if (!current) {
        return current;
      }

      const hasGroupedOrders = current.orders.some(
        (candidate) =>
          Array.isArray(candidate.aggregatedIds) &&
          candidate.aggregatedIds.length > 0,
      );

      if (hasGroupedOrders) {
        return current;
      }

      return {
        ...current,
        orders: current.orders.filter((order) => order.id !== orderId),
      };
    });
  }, []);

  const updateOrderStatusesInOrdersPage = useCallback(
    (ids: string[], status: OrderStatus) => {
      if (ids.length === 0) {
        return;
      }

      const idSet = new Set(ids);

      setOrdersPage((current) => {
        if (!current) {
          return current;
        }

        const hasGroupedOrders = current.orders.some(
          (order) =>
            Array.isArray(order.aggregatedIds) &&
            order.aggregatedIds.length > 0,
        );

        if (hasGroupedOrders) {
          return current;
        }

        return {
          ...current,
          orders: current.orders.flatMap((order) => {
            if (!idSet.has(order.id)) {
              return [order];
            }

            const updatedOrder = { ...order, status };

            return doesOrderMatchCurrentList(updatedOrder) ? [updatedOrder] : [];
          }),
        };
      });
    },
    [doesOrderMatchCurrentList],
  );

  const loadStoresPage = useCallback(
    async (force = false) => {
      if (storesPage && !force) {
        return storesPage;
      }

      setLoadingTab('STORES');
      setTabError(null);

      try {
        const nextPage = await getSellerStoresPage();
        setStoresPage(nextPage);
        return nextPage;
      } catch (error) {
        console.error('Failed to load stores page:', error);
        setTabError('배송지 관리 데이터를 불러오지 못했습니다.');
        return null;
      } finally {
        setLoadingTab((current) => (current === 'STORES' ? null : current));
      }
    },
    [storesPage],
  );

  const loadStoreMetricsPage = useCallback(async (force = false) => {
    if (storeMetricsPage && !force) {
      return storeMetricsPage;
    }

    try {
      const nextPage = await getSellerStoreMetricsPage();
      setStoreMetricsPage(nextPage);
      return nextPage;
    } catch (error) {
      console.error('Failed to load store metrics page:', error);
      return null;
    }
  }, [storeMetricsPage]);

  const loadProductMetaPage = useCallback(async (force = false) => {
    if (productMetaPage && !force) {
      return productMetaPage;
    }

    try {
      const nextPage = await getSellerProductMetaPage();
      setProductMetaPage(nextPage);
      return nextPage;
    } catch (error) {
      console.error('Failed to load product meta page:', error);
      return null;
    }
  }, [productMetaPage]);

  const loadProductMetricsPage = useCallback(
    async (
      force = false,
      options?: {
        productIds?: string[];
        orderSheetIds?: string[];
      },
    ) => {
      const productIds = options?.productIds || [];
      const orderSheetIds = options?.orderSheetIds;

      if (productIds.length === 0) {
        setProductMetricsPage(EMPTY_PRODUCT_METRICS_PAGE);
        return EMPTY_PRODUCT_METRICS_PAGE;
      }

      if (productMetricsPage && !force) {
        return productMetricsPage;
      }

      try {
        const nextPage = await getSellerProductMetricsPage({
          productIds,
          orderSheetIds,
        });
        setProductMetricsPage(nextPage);

        return nextPage;
      } catch (error) {
        console.error('Failed to load product metrics page:', error);
        setTabError('상품 관리 데이터를 불러오지 못했습니다.');
        return null;
      }
    },
    [productMetricsPage],
  );

  const loadOrderMetaPage = useCallback(async (force = false) => {
    if (orderMetaPage && !force) {
      return orderMetaPage;
    }

    try {
      const nextPage = await getSellerOrderMetaPage();
      setOrderMetaPage(nextPage);
      return nextPage;
    } catch (error) {
      console.error('Failed to load order meta page:', error);
      setTabError('주문 관리 메타 데이터를 불러오지 못했습니다.');
      return null;
    }
  }, [orderMetaPage]);

  const loadOrderProductsPage = useCallback(async (force = false) => {
    if (orderProductsPage && !force) {
      return orderProductsPage;
    }

    try {
      const nextPage = await getSellerOrderProductsPage();
      setOrderProductsPage(nextPage);
      return nextPage;
    } catch (error) {
      console.error('Failed to load order products page:', error);
      setTabError('주문용 상품 데이터를 불러오지 못했습니다.');
      return null;
    }
  }, [orderProductsPage]);

  const loadProductsPage = useCallback(
    async (options?: {
      force?: boolean;
      forceMeta?: boolean;
      selectedOrderSheetIds?: string[];
      searchTerm?: string;
      activeTag?: string | null;
      sortBy?: SellerProductSortBy;
      page?: number;
    }) => {
      const force = options?.force || false;
      const forceMeta = options?.forceMeta || false;
      const selectedOrderSheetIds =
        options?.selectedOrderSheetIds || productSelectedOrderSheetIds;
      const searchTerm =
        options?.searchTerm !== undefined
          ? options.searchTerm
          : debouncedProductSearchTerm;
      const activeTag =
        options?.activeTag !== undefined ? options.activeTag : productActiveTag;
      const sortBy =
        options?.sortBy !== undefined ? options.sortBy : productSortBy;
      const page =
        options?.page !== undefined ? options.page : productCurrentPage;
      const filterKey = getProductFilterKey({
        searchTerm,
        activeTag,
        sortBy,
        selectedOrderSheetIds,
        page,
      });

      if (productsPage && productMetaPage && productMetricsPage && !force) {
        return productsPage;
      }

      if (
        !force &&
        !forceMeta &&
        productPagePromiseRef.current &&
        productPagePromiseKeyRef.current === filterKey
      ) {
        return productPagePromiseRef.current;
      }

      setLoadingTab('PRODUCTS');
      setTabError(null);
      const promise = (async () => {
        try {
          const [resolvedProductMetaPage, nextPage] = await Promise.all([
            forceMeta
              ? loadProductMetaPage(true)
              : productMetaPage
              ? Promise.resolve(productMetaPage)
              : loadProductMetaPage(),
            getSellerProductsPage({
              orderSheetIds: getProductOrderSheetRequestIds(selectedOrderSheetIds),
              sortOrderSheetIds:
                sortBy !== 'DEFAULT'
                  ? getProductMetricOrderSheetRequestIds(
                      selectedOrderSheetIds,
                      productMetaPage?.orderSheets,
                    )
                  : undefined,
              search: searchTerm,
              tag: activeTag,
              sortBy,
              page,
              take: PRODUCTS_PAGE_SIZE,
            }),
          ]);

          if (!resolvedProductMetaPage) {
            throw new Error('Failed to load product meta page.');
          }

          const metricOrderSheetIds = getProductMetricOrderSheetRequestIds(
            selectedOrderSheetIds,
            resolvedProductMetaPage.orderSheets,
          );
          const nextMetricsPage = await loadProductMetricsPage(true, {
            productIds: nextPage.products.map((product) => product.id),
            orderSheetIds: metricOrderSheetIds,
          });

          if (!nextMetricsPage) {
            throw new Error('Failed to load product metrics page.');
          }

          setProductsPage(nextPage);
          setProductCurrentPage(nextPage.currentPage);
          lastLoadedProductFilterKeyRef.current = getProductFilterKey({
            searchTerm,
            activeTag,
            sortBy,
            selectedOrderSheetIds,
            page: nextPage.currentPage,
          });

          return nextPage;
        } catch (error) {
          console.error('Failed to load products page:', error);
          setTabError('상품 관리 데이터를 불러오지 못했습니다.');
          return null;
        } finally {
          if (productPagePromiseRef.current === promise) {
            productPagePromiseRef.current = null;
            productPagePromiseKeyRef.current = '';
          }
          setLoadingTab((current) => (current === 'PRODUCTS' ? null : current));
        }
      })();

      productPagePromiseRef.current = promise;
      productPagePromiseKeyRef.current = filterKey;

      return promise;
    },
    [
      debouncedProductSearchTerm,
      getProductFilterKey,
      getProductMetricOrderSheetRequestIds,
      getProductOrderSheetRequestIds,
      loadProductMetaPage,
      loadProductMetricsPage,
      productActiveTag,
      productCurrentPage,
      productMetaPage,
      productMetricsPage,
      productSelectedOrderSheetIds,
      productSortBy,
      productsPage,
    ],
  );

  const getOrderSheetRequestIds = useCallback((ids: string[]) => {
    return ids.includes('all') ? undefined : ids;
  }, []);

  const loadOrdersPage = useCallback(
    async (options?: {
      force?: boolean;
      selectedOrderSheetIds?: string[];
      searchTerm?: string;
      statusFilter?: 'ALL' | OrderStatus;
      sortBy?: SellerOrderSortBy;
      page?: number;
      storeId?: string;
    }) => {
      const force = options?.force || false;
      const selectedOrderSheetIds =
        options?.selectedOrderSheetIds || orderSelectedOrderSheetIds;
      const searchTerm =
        options?.searchTerm !== undefined
          ? options.searchTerm
          : debouncedOrderSearchTerm;
      const statusFilter =
        options?.statusFilter !== undefined
          ? options.statusFilter
          : orderStatusFilter;
      const sortBy =
        options?.sortBy !== undefined ? options.sortBy : orderSortBy;
      const page =
        options?.page !== undefined ? options.page : orderCurrentPage;
      const storeId =
        options?.storeId !== undefined
          ? options.storeId
          : options?.selectedOrderSheetIds !== undefined
            ? undefined
            : orderSelectedStoreId;
      const filterKey = getOrderFilterKey({
        selectedOrderSheetIds,
        searchTerm,
        statusFilter,
        storeId,
        sortBy,
        page,
      });

      if (ordersPage && !force) {
        return ordersPage;
      }

      setLoadingTab('ORDERS');
      setTabError(null);

      try {
        const nextPage = await getSellerOrdersPage({
          orderSheetIds: getOrderSheetRequestIds(selectedOrderSheetIds),
          search: searchTerm,
          status: statusFilter,
          sortBy,
          page,
          take: ORDERS_PAGE_SIZE,
          storeId,
        });
        setOrdersPage(nextPage);
        setOrderCurrentPage(nextPage.currentPage);
        lastLoadedOrderFilterKeyRef.current = filterKey;

        return nextPage;
      } catch (error) {
        console.error('Failed to load orders page:', error);
        setTabError('주문 관리 데이터를 불러오지 못했습니다.');
        return null;
      } finally {
        setLoadingTab((current) => (current === 'ORDERS' ? null : current));
      }
    },
    [
      debouncedOrderSearchTerm,
      getOrderFilterKey,
      getOrderSheetRequestIds,
      orderCurrentPage,
      orderSelectedOrderSheetIds,
      orderSelectedStoreId,
      orderSortBy,
      orderStatusFilter,
      ordersPage,
    ],
  );

  const fetchAllOrdersForSelection = useCallback(async () => {
    const allOrders: Order[] = [];
    let page = 1;

    while (true) {
      const result = await getSellerOrdersPage({
        orderSheetIds: getOrderSheetRequestIds(orderSelectedOrderSheetIds),
        search: debouncedOrderSearchTerm,
        status: orderStatusFilter,
        sortBy: orderSortBy,
        page,
        take: ORDER_SELECTION_PAGE_SIZE,
        storeId: orderSelectedStoreId,
      });

      allOrders.push(...result.orders);

      if (!result.hasMore || page >= result.totalPages || result.orders.length === 0) {
        break;
      }

      page += 1;
    }

    return allOrders;
  }, [
    debouncedOrderSearchTerm,
    getOrderSheetRequestIds,
    orderSelectedOrderSheetIds,
    orderSelectedStoreId,
    orderSortBy,
    orderStatusFilter,
  ]);

  const loadCustomersPage = useCallback(
    async (force = false) => {
      if (customersPage && !force) {
        return customersPage;
      }

      setLoadingTab('CUSTOMERS');
      setTabError(null);

      try {
        const nextPage = await getSellerCustomersPage();
        setCustomersPage(nextPage);
        return nextPage;
      } catch (error) {
        console.error('Failed to load customers page:', error);
        setTabError('고객 관리 데이터를 불러오지 못했습니다.');
        return null;
      } finally {
        setLoadingTab((current) => (current === 'CUSTOMERS' ? null : current));
      }
    },
    [customersPage],
  );

  const runOrdersTabBundle = useCallback(
    async (loader: () => Promise<unknown>) => {
      if (ordersBundlePromiseRef.current) {
        return ordersBundlePromiseRef.current;
      }
      setIsOrdersBundleLoading(true);
      setTabError(null);
      const promise = (async () => {
        try {
          return await loader();
        } finally {
          ordersBundlePromiseRef.current = null;
          setIsOrdersBundleLoading(false);
        }
      })();

      ordersBundlePromiseRef.current = promise;

      return promise;
    },
    [],
  );

  useEffect(() => {
    if (activeTab === 'STORES') {
      void loadStoresPage();
      void loadStoreMetricsPage();
    }

    if (activeTab === 'PRODUCTS') {
      if (
        productPagePromiseRef.current ||
        (productMetaPage && productsPage && productMetricsPage)
      ) {
        return;
      }

      void loadProductsPage();
    }

    if (activeTab === 'ORDERS') {
      if (
        ordersBundlePromiseRef.current ||
        (orderMetaPage && orderProductsPage && ordersPage)
      ) {
        return;
      }

      void runOrdersTabBundle(async () => {
        await Promise.all([
          loadOrderMetaPage(),
          loadOrderProductsPage(),
          loadOrdersPage(),
        ]);
      });
    }

    if (activeTab === 'CUSTOMERS') {
      void loadCustomersPage();
    }

  }, [
    activeTab,
    loadCustomersPage,
    loadOrderMetaPage,
    loadOrderProductsPage,
    loadOrdersPage,
    productMetaPage,
    productMetricsPage,
    productsPage,
    orderMetaPage,
    orderProductsPage,
    ordersPage,
    loadProductsPage,
    runOrdersTabBundle,
    loadStoreMetricsPage,
    loadStoresPage,
  ]);

  useEffect(() => {
    if (activeTab !== 'PRODUCTS' || !productsPage) {
      return;
    }

    if (productSearchTerm.trim() !== debouncedProductSearchTerm) {
      return;
    }

    const currentFilterKey = getProductFilterKey();

    if (lastLoadedProductFilterKeyRef.current === currentFilterKey) {
      return;
    }

    void loadProductsPage({ force: true });
  }, [
    activeTab,
    debouncedProductSearchTerm,
    getProductFilterKey,
    loadProductsPage,
    productActiveTag,
    productSearchTerm,
    productSelectedOrderSheetIds,
    productSortBy,
    productsPage,
  ]);

  useEffect(() => {
    if (activeTab !== 'ORDERS' || !ordersPage) {
      return;
    }

    const currentFilterKey = getOrderFilterKey();

    if (lastLoadedOrderFilterKeyRef.current === currentFilterKey) {
      return;
    }

    void loadOrdersPage({ force: true });
  }, [
    activeTab,
    debouncedOrderSearchTerm,
    getOrderFilterKey,
    loadOrdersPage,
    orderCurrentPage,
    orderSelectedOrderSheetIds,
    orderSelectedStoreId,
    orderSortBy,
    orderStatusFilter,
    ordersPage,
  ]);

  const refreshAfterStoreChange = useCallback(async () => {
    invalidateTabs(['DASHBOARD', 'STORES', 'PRODUCTS', 'ORDERS', 'CUSTOMERS']);
    invalidateProductMeta();
    invalidateOrderMeta();
    invalidateStoreMetrics();
    await loadStoresPage(true);
    await loadStoreMetricsPage(true);
  }, [
    invalidateOrderMeta,
    invalidateProductMeta,
    invalidateStoreMetrics,
    invalidateTabs,
    loadStoreMetricsPage,
    loadStoresPage,
  ]);

  const refreshAfterProductChange = useCallback(async () => {
    invalidateTabs(['DASHBOARD', 'PRODUCTS', 'ORDERS']);
    invalidateProductMeta();
    invalidateProductMetrics();
    invalidateOrderProducts();
    await loadProductsPage({ force: true, forceMeta: true });
  }, [
    invalidateOrderProducts,
    invalidateProductMeta,
    invalidateProductMetrics,
    invalidateTabs,
    loadProductsPage,
  ]);

  const refreshAfterOrderChange = useCallback(async () => {
    const shouldReloadProductsPage =
      productSortBy !== 'DEFAULT' ||
      !productSelectedOrderSheetIds.includes('all');

    invalidateTabs(
      shouldReloadProductsPage
        ? ['DASHBOARD', 'PRODUCTS', 'ORDERS', 'CUSTOMERS']
        : ['DASHBOARD', 'ORDERS', 'CUSTOMERS'],
    );
    invalidateProductMetrics();
    invalidateOrderProducts();
    invalidateStoreMetrics();

    if (activeTab === 'CUSTOMERS') {
      await loadCustomersPage(true);
      return;
    }

    if (activeTab === 'PRODUCTS') {
      if (shouldReloadProductsPage) {
        await loadProductsPage({ force: true });
        return;
      }

      await loadProductMetricsPage(true, {
        productIds: (productsPage?.products || EMPTY_PRODUCTS).map(
          (product) => product.id,
        ),
        orderSheetIds: getProductMetricOrderSheetRequestIds(
          productSelectedOrderSheetIds,
          productMetaPage?.orderSheets,
        ),
      });
      return;
    }

    if (activeTab === 'STORES') {
      await loadStoreMetricsPage(true);
      return;
    }

    if (activeTab === 'ORDERS') {
      await runOrdersTabBundle(async () => {
        await Promise.all([
          loadOrderProductsPage(true),
          loadOrdersPage({ force: true }),
        ]);
      });
      return;
    }

    await loadOrdersPage({ force: true });
  }, [
    activeTab,
    invalidateOrderProducts,
    invalidateStoreMetrics,
    invalidateTabs,
    getProductMetricOrderSheetRequestIds,
    getProductOrderSheetRequestIds,
    loadCustomersPage,
    loadOrderProductsPage,
    loadOrdersPage,
    loadProductsPage,
    loadProductMetricsPage,
    loadStoreMetricsPage,
    productMetaPage?.orderSheets,
    productSelectedOrderSheetIds,
    productSortBy,
    productsPage?.products,
    runOrdersTabBundle,
  ]);

  const refreshAfterAliasChange = useCallback(async () => {
    invalidateTabs(['ORDERS', 'CUSTOMERS']);
    invalidateOrderMeta();

    if (activeTab === 'CUSTOMERS') {
      await loadCustomersPage(true);
      return;
    }

    await loadOrderMetaPage(true);
  }, [
    activeTab,
    invalidateOrderMeta,
    invalidateTabs,
    loadCustomersPage,
    loadOrderMetaPage,
  ]);

  const refreshOrdersTabData = useCallback(async () => {
    invalidateTabs(['ORDERS', 'PRODUCTS']);
    invalidateProductMeta();
    invalidateOrderMeta();
    invalidateOrderProducts();

    await runOrdersTabBundle(async () => {
      await Promise.all([
        loadOrderMetaPage(true),
        loadOrderProductsPage(true),
        loadOrdersPage({ force: true }),
      ]);
    });
  }, [
    invalidateOrderMeta,
    invalidateOrderProducts,
    invalidateProductMeta,
    invalidateTabs,
    loadOrderMetaPage,
    loadOrderProductsPage,
    loadOrdersPage,
    runOrdersTabBundle,
  ]);

  const handleOpenAdminOrder = (
    name = '',
    phone = '',
    order?: Order,
    storeId = '',
    orderSheetId = '',
    userId = '',
  ) => {
    setAdminOrderInitialCustomer({ name, phone, userId });
    setEditingOrder(order);
    setAdminOrderInitialStoreId(storeId);
    setAdminOrderInitialOrderSheetId(orderSheetId);
    setIsAdminOrderModalOpen(true);
  };

  const handleOpenHistory = (
    name: string,
    phone: string,
    userId?: string,
    orderSheetId?: string,
  ) => {
    setHistoryCustomer({ name, phone, userId, orderSheetId });
  };

  const handleOpenHistoryFromProduct = (
    name: string,
    phone: string,
    userId?: string,
    orderSheetId?: string,
  ) => {
    setOrderCurrentPage(1);
    if (orderSheetId) {
      setOrderSelectedStoreId(undefined);
      setOrderSelectedOrderSheetIds([orderSheetId]);
    }
    navigateToTab('ORDERS');
    setHistoryCustomer({ name, phone, userId, orderSheetId });
  };

  const handleProductSearchTermChange = useCallback((value: string) => {
    setProductCurrentPage(1);
    setProductSearchTerm(value);
  }, []);

  const handleProductActiveTagChange = useCallback((value: string | null) => {
    setProductCurrentPage(1);
    setProductActiveTag(value);
  }, []);

  const handleProductSortChange = useCallback((value: SellerProductSortBy) => {
    setProductCurrentPage(1);
    setProductSortBy(value);
  }, []);

  const handleProductOrderSheetChange = useCallback((ids: string[]) => {
    setProductCurrentPage(1);
    setProductSelectedOrderSheetIds(ids);
  }, []);

  const handleProductPageChange = useCallback((page: number) => {
    setProductCurrentPage(page);
  }, []);

  const handleOrderSearchTermChange = useCallback((value: string) => {
    setOrderCurrentPage(1);
    setOrderSearchTerm(value);
  }, []);

  const handleOrderStatusFilterChange = useCallback(
    (status: 'ALL' | OrderStatus) => {
      setOrderCurrentPage(1);
      setOrderStatusFilter(status);
    },
    [],
  );

  const handleOrderSortChange = useCallback((value: SellerOrderSortBy) => {
    setOrderCurrentPage(1);
    setOrderSortBy(value);
  }, []);

  const handleOrderSheetSelectionChange = useCallback(
    (ids: string[]) => {
      setOrderCurrentPage(1);
      setOrderSelectedOrderSheetIds(ids);
      setOrderSelectedStoreId(undefined);
      setOrderStatusFilter('ALL');

      if (activeTab === 'ORDERS') {
        void loadOrdersPage({
          force: true,
          selectedOrderSheetIds: ids,
          statusFilter: 'ALL',
          page: 1,
        });
      }
    },
    [activeTab, loadOrdersPage],
  );

  const handleOrderPageChange = useCallback((page: number) => {
    setOrderCurrentPage(page);
  }, []);

  const productOrderSheets = productMetaPage?.orderSheets || EMPTY_ORDER_SHEETS;
  const orderPageOrderSheets = orderMetaPage?.orderSheets || EMPTY_ORDER_SHEETS;
  const mergedProductsPageProducts = useMemo(
    () => mergeProductsWithMetrics(productsPage?.products || EMPTY_PRODUCTS, productMetricsPage),
    [productMetricsPage, productsPage?.products],
  );

  useEffect(() => {
    if (
      productSelectedOrderSheetIds.length === 0 &&
      productOrderSheets.length > 0
    ) {
      setProductSelectedOrderSheetIds([productOrderSheets[0].id]);
    }
  }, [productOrderSheets, productSelectedOrderSheetIds]);

  useEffect(() => {
    if (
      orderSelectedOrderSheetIds.length === 0 &&
      orderPageOrderSheets.length > 0
    ) {
      setOrderSelectedOrderSheetIds([orderPageOrderSheets[0].id]);
    }
  }, [orderPageOrderSheets, orderSelectedOrderSheetIds]);

  useEffect(() => {
    const handlePopState = () => {
      if (isEditorOpen) {
        setIsEditorOpen(false);
        setEditingProduct(null);
      }
      if (historyCustomer) {
        setHistoryCustomer(null);
      }
    };

    if (isEditorOpen || historyCustomer) {
      window.history.pushState({ modal: true }, '');
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [historyCustomer, isEditorOpen]);

  const modalStores =
    orderMetaPage?.stores ||
    customersPage?.stores ||
    productMetaPage?.stores ||
    storesPage?.stores ||
    EMPTY_STORES;
  const modalProducts = orderProductsPage?.products || EMPTY_PRODUCTS;
  const modalOrderSheets =
    orderMetaPage?.orderSheets ||
    customersPage?.orderSheets ||
    productMetaPage?.orderSheets ||
    EMPTY_ORDER_SHEETS;

  const handleOpenEditor = (product?: Product) => {
    setEditingProduct(
      product || {
        id: `p-${Date.now()}`,
        name: '',
        shortName: '',
        price: 0,
        stocks: (productMetaPage?.stores || EMPTY_STORES).reduce(
          (acc, store) => ({ ...acc, [store.id]: 0 }),
          {},
        ),
        image: '',
        description: '',
        isClosed: false,
        isCustomerCancellationEnabled: true,
      },
    );
    setIsEditorOpen(true);
  };

  const handleSaveProduct = async (productData?: any) => {
    if (!productData) {
      setIsEditorOpen(false);
      setEditingProduct(null);
      await refreshAfterProductChange();
      return;
    }

    const { id, isOptionMode, options: rawOptions, ...rest } = productData;
    const { name, price } = rest;

    if (!name || price === undefined) {
      alert('상품명과 판매가를 입력해주세요.');
      return;
    }

    const payload = {
      ...rest,
      options:
        isOptionMode && rawOptions && rawOptions.length > 0
          ? rawOptions.map((option: any, index: number) => ({
              id: option.id,
              name: option.name,
              price: option.price ?? null,
              stock: option.stock || 0,
              position: option.position ?? index,
            }))
          : [],
    };
    const isNew = !id || String(id).startsWith('p-');

    try {
      if (isNew) {
        await createProduct(payload);
        alert('상품이 추가되었습니다.');
      } else {
        const updatedProduct = await updateProductWithStockConfirmation(
          id,
          payload,
        );
        if (!updatedProduct) {
          return;
        }
        alert('상품이 수정되었습니다.');
      }

      setIsEditorOpen(false);
      setEditingProduct(null);
      await refreshAfterProductChange();
    } catch (error) {
      console.error('Failed to save product:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteProduct(id);
      await refreshAfterProductChange();
    } catch (error) {
      console.error('Failed to delete product:', error);
      alert('상품 삭제에 실패했습니다.');
    }
  };

  const handleUpdateOrderStatus = async (order: Order) => {
    try {
      const updatedOrder = await updateOrderStatus(order.id, order.status, 'SELLER');

      upsertOrderInOrdersPage(updatedOrder);
      invalidateOrderRelatedTabsExceptOrders();
      invalidateOrderProducts();

      if (activeTab !== 'ORDERS') {
        await refreshAfterOrderChange();
        return;
      }

      void loadOrderProductsPage(true);
      void loadOrdersPage({ force: true });
    } catch (error) {
      console.error('Failed to update order status:', error);
      alert('주문 상태 변경에 실패했습니다.');
    }
  };

  const handleUpdateOrderStatusBatch = async (
    ids: string[],
    status: OrderStatus,
    modifierRole?: string,
    partialUpdates?: { id: string; itemIds: string[] }[],
  ) => {
    try {
      await updateOrderStatusBatch(
        ids,
        status,
        modifierRole || 'SELLER',
        partialUpdates,
      );

      const shouldRefresh =
        activeTab !== 'ORDERS' || (partialUpdates?.length || 0) > 0;

      if (shouldRefresh) {
        await refreshAfterOrderChange();
      } else {
        updateOrderStatusesInOrdersPage(ids, status);
        invalidateOrderRelatedTabsExceptOrders();
        invalidateOrderProducts();
        void loadOrderProductsPage(true);
        void loadOrdersPage({ force: true });
      }

      const count = (ids?.length || 0) + (partialUpdates?.length || 0);
      alert(`${count}건의 주문(또는 상품) 상태가 변경되었습니다.`);
    } catch (error) {
      console.error('Failed to update status in batch:', error);
      alert('일괄 상태 변경에 실패했습니다.');
    }
  };

  const handleOrderMutated = useCallback(
    (order: Order) => {
      upsertOrderInOrdersPage(order);
      invalidateOrderRelatedTabsExceptOrders();
      invalidateOrderProducts();
      void loadOrderProductsPage(true);
      void loadOrdersPage({ force: true });
    },
    [
      invalidateOrderProducts,
      invalidateOrderRelatedTabsExceptOrders,
      loadOrderProductsPage,
      loadOrdersPage,
      upsertOrderInOrdersPage,
    ],
  );

  const handleOrderDeleted = useCallback(
    (orderId: string) => {
      removeOrderFromOrdersPage(orderId);
      invalidateOrderRelatedTabsExceptOrders();
      invalidateOrderProducts();
      void runOrdersTabBundle(async () => {
        await Promise.all([
          loadOrderProductsPage(true),
          loadOrdersPage({ force: true }),
        ]);
      });
    },
    [
      invalidateOrderProducts,
      invalidateOrderRelatedTabsExceptOrders,
      loadOrderProductsPage,
      loadOrdersPage,
      removeOrderFromOrdersPage,
      runOrdersTabBundle,
    ],
  );

  const handleSettle = async (storeId?: string) => {
    if (!confirm('현재 진행 중인 주간 영업을 마감하시겠습니까?')) {
      return;
    }

    try {
      await settleOrderSheet(storeId);
      alert('영업 마감이 완료되었습니다.');
      invalidateTabs(['PRODUCTS']);
      invalidateProductMeta();
      invalidateProductMetrics();
      invalidateOrderMeta();
      await refreshAfterOrderChange();

      if (activeTab === 'ORDERS') {
        await runOrdersTabBundle(async () => {
          await loadOrderMetaPage(true);
        });
      }
    } catch (error) {
      console.error('Settlement failed', error);
      alert('영업 마감 처리에 실패했습니다.');
    }
  };

  const handleUpdateAlias = async (phone: string, nick: string) => {
    try {
      await updateCustomerAlias(phone, nick);
      await refreshAfterAliasChange();
    } catch (error) {
      console.error('Failed to update customer alias:', error);
      alert('닉네임 설정에 실패했습니다.');
    }
  };

  const renderLoadingState = () => (
    <div className="bg-white rounded-[32px] border shadow-sm p-12 text-center text-gray-400 font-bold">
      데이터를 불러오는 중입니다...
    </div>
  );

  const renderErrorState = (tab: ActiveTab) => (
    <div className="bg-white rounded-[32px] border shadow-sm p-8 text-center space-y-4">
      <p className="text-sm font-black text-gray-900">
        {tabError || '페이지 데이터를 불러오지 못했습니다.'}
      </p>
      <button
        onClick={() => {
          if (tab === 'STORES') {
            void loadStoresPage(true);
            void loadStoreMetricsPage(true);
          }
          if (tab === 'PRODUCTS') {
            void loadProductsPage({ force: true });
          }
          if (tab === 'ORDERS') {
            void runOrdersTabBundle(async () => {
              await Promise.all([
                loadOrderMetaPage(true),
                loadOrderProductsPage(true),
                loadOrdersPage({ force: true }),
              ]);
            });
          }
          if (tab === 'CUSTOMERS') {
            void loadCustomersPage(true);
          }
        }}
        className="h-10 px-4 rounded-xl bg-gray-900 text-white text-xs font-black"
      >
        다시 불러오기
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans">
      <div className="hidden md:flex w-64 bg-gray-900 text-white flex-col shrink-0 print:hidden">
        <div className="p-8">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-emerald-500 rounded-2xl flex items-center justify-center">
              <TrendingUp className="text-white" size={20} />
            </div>
            <h1 className="text-xl font-black tracking-tight">OAS Market</h1>
          </div>

          <nav className="space-y-1">
            {VISIBLE_TAB_ITEMS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigateToTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === tab.id
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <tab.icon size={20} />
                <span className="text-sm font-bold">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-auto p-6 space-y-4">
          <button
            onClick={() => navigateToTab('SETTINGS')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === 'SETTINGS'
                ? 'bg-white/10 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Settings size={20} />
            <span className="text-sm font-bold">환경설정</span>
          </button>
          <div className="pt-4 border-t border-white/5">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all font-bold text-sm"
            >
              <LogOut size={20} />
              로그아웃
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        <div className="md:hidden bg-gray-900 text-white px-6 pt-12 pb-6 flex items-center justify-between shrink-0 print:hidden">
          <div className="flex items-center gap-3">
            <button
              onClick={onExit}
              className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-xl font-black">판매자 대시보드</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigateToTab('SETTINGS')}
              className={`p-2 rounded-full transition-colors ${
                activeTab === 'SETTINGS'
                  ? 'bg-white text-gray-900'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <Settings size={18} />
            </button>
            <button
              onClick={() => window.location.reload()}
              className="p-2 bg-white/10 rounded-full"
            >
              <RefreshCw size={18} />
            </button>
            <button
              onClick={onLogout}
              className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        <header className="hidden md:flex h-20 items-center justify-between px-10 bg-white border-b shrink-0 print:hidden">
          <div>
            <h2 className="text-xl font-black text-gray-900">{SELLER_TAB_LABELS[activeTab]}</h2>
            <p className="text-xs text-gray-400 font-bold mt-0.5">
              환영합니다, {user?.name || '판매자'}님
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.location.reload()}
              className="p-2.5 text-gray-400 hover:text-gray-900 bg-gray-50 rounded-xl transition-colors"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={onExit}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl font-black text-xs hover:bg-gray-800 transition-all shadow-lg shadow-gray-900/10"
            >
              <ArrowLeft size={16} /> 상점으로 돌아가기
            </button>
          </div>
        </header>

        <div className="md:hidden flex bg-white border-b px-2 sticky top-0 z-40 shrink-0 print:hidden overflow-x-auto no-scrollbar">
          {VISIBLE_TAB_ITEMS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigateToTab(tab.id)}
              className={`min-w-[74px] flex-1 py-4 flex flex-col items-center gap-1 transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <tab.icon size={22} />
              <span className="text-[10px] font-black uppercase tracking-tighter">
                {tab.label.replace(' 관리', '')}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto w-full max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-10 space-y-6 md:space-y-10">
          {activeTab === 'DASHBOARD' && (
            <DashboardTab
              onViewOrders={() => {
                setOrderCurrentPage(1);
                navigateToTab('ORDERS');
              }}
              onViewProducts={() => {
                setProductCurrentPage(1);
                navigateToTab('PRODUCTS');
              }}
              refreshToken={dashboardRefreshToken}
            />
          )}

          {activeTab === 'STORES' &&
            (loadingTab === 'STORES' && !storesPage ? (
              renderLoadingState()
            ) : !storesPage ? (
              renderErrorState('STORES')
            ) : (
              <StoresTab
                stores={storesPage.stores}
                deletedStores={storesPage.deletedStores}
                storeMetrics={storeMetricsPage?.metricsByStore}
                onRefreshPage={refreshAfterStoreChange}
                onViewOrders={(storeId) => {
                  setOrderCurrentPage(1);
                  setOrderSelectedStoreId(storeId);
                  setOrderSelectedOrderSheetIds(['all']);
                  navigateToTab('ORDERS');
                }}
                onPrint={(storeId) => {
                  setOrderCurrentPage(1);
                  setOrderSelectedStoreId(storeId);
                  setOrderSelectedOrderSheetIds(['all']);
                  navigateToTab('ORDERS');
                }}
              />
            ))}

          {activeTab === 'PRODUCTS' &&
            (loadingTab === 'PRODUCTS' &&
            (!productMetaPage || !productsPage || !productMetricsPage) ? (
              renderLoadingState()
            ) : !productMetaPage || !productsPage || !productMetricsPage ? (
              renderErrorState('PRODUCTS')
            ) : (
              <ProductsTab
                onOpenEditor={handleOpenEditor}
                products={mergedProductsPageProducts}
                orderMetricsBySheet={productMetricsPage.orderMetricsBySheet}
                stores={productMetaPage.stores}
                searchTerm={productSearchTerm}
                setSearchTerm={handleProductSearchTermChange}
                activeTag={productActiveTag}
                onActiveTagChange={handleProductActiveTagChange}
                sortBy={productSortBy}
                onSortByChange={handleProductSortChange}
                currentPage={productsPage.currentPage}
                totalPages={productsPage.totalPages}
                totalCount={productsPage.totalCount}
                onPageChange={handleProductPageChange}
                orderSheets={productMetaPage.orderSheets}
                availableTags={productMetaPage.tags}
                onRefresh={refreshAfterProductChange}
                selectedOrderSheetIds={productSelectedOrderSheetIds}
                onOrderSheetChange={handleProductOrderSheetChange}
                onDeleteProduct={handleDeleteProduct}
                onViewCustomerHistory={handleOpenHistoryFromProduct}
              />
            ))}

          {activeTab === 'ORDERS' &&
            ((loadingTab === 'ORDERS' || isOrdersBundleLoading) &&
            (!orderMetaPage || !orderProductsPage || !ordersPage) ? (
              renderLoadingState()
            ) : !orderMetaPage || !orderProductsPage || !ordersPage ? (
              renderErrorState('ORDERS')
            ) : (
              <OrdersTab
                orders={ordersPage.orders}
                ordersSummary={ordersPage.summary}
                stores={orderMetaPage.stores}
                orderSearchTerm={orderSearchTerm}
                setOrderSearchTerm={handleOrderSearchTermChange}
                statusFilter={orderStatusFilter}
                onStatusFilterChange={handleOrderStatusFilterChange}
                sortBy={orderSortBy}
                onSortByChange={handleOrderSortChange}
                statusCounts={ordersPage.statusCounts}
                onUpdateOrderStatus={handleUpdateOrderStatus}
                onUpdateOrderStatusBatch={handleUpdateOrderStatusBatch}
                orderSheets={orderMetaPage.orderSheets}
                selectedOrderSheetIds={orderSelectedOrderSheetIds}
                onOrderSheetChange={handleOrderSheetSelectionChange}
                products={orderProductsPage.products}
                currentPage={ordersPage.currentPage}
                totalPages={ordersPage.totalPages}
                totalCount={ordersPage.totalCount}
                onPageChange={handleOrderPageChange}
                selectedStoreId={orderSelectedStoreId}
                onFetchAllOrdersForSelection={fetchAllOrdersForSelection}
                onRefresh={refreshOrdersTabData}
                onSettle={handleSettle}
                customerAliases={orderMetaPage.customerAliases}
                onOpenAdminOrder={(
                  name,
                  phone,
                  order,
                  storeId,
                  orderSheetId,
                  userId,
                ) =>
                  handleOpenAdminOrder(
                    name,
                    phone,
                    order,
                    storeId,
                    orderSheetId,
                    userId,
                  )
                }
                onViewHistory={handleOpenHistory}
              />
            ))}

          {activeTab === 'CUSTOMERS' &&
            (loadingTab === 'CUSTOMERS' && !customersPage ? (
              renderLoadingState()
            ) : !customersPage ? (
              renderErrorState('CUSTOMERS')
            ) : (
              <CustomersTab
                customerSummaries={customersPage.customerSummaries}
                stores={customersPage.stores}
                orderSheets={customersPage.orderSheets}
                customerAliases={customersPage.customerAliases}
                onUpdateAlias={handleUpdateAlias}
                onOpenAdminOrder={(
                  name,
                  phone,
                  order,
                  storeId,
                  orderSheetId,
                  userId,
                ) =>
                  handleOpenAdminOrder(
                    name,
                    phone,
                    order,
                    storeId,
                    orderSheetId,
                    userId,
                  )
                }
                onUpdateOrderStatus={handleUpdateOrderStatus}
                onViewHistory={handleOpenHistory}
              />
            ))}

          {activeTab === 'LOGS' && <HistoryTab />}

          {activeTab === 'SETTINGS' && <SettingsTab />}
        </div>

        {isEditorOpen && editingProduct && (
          <ProductEditor
            editingProduct={editingProduct}
            setEditingProduct={setEditingProduct}
            onSave={handleSaveProduct}
            onClose={() => {
              setIsEditorOpen(false);
              setEditingProduct(null);
            }}
            stores={productMetaPage?.stores || EMPTY_STORES}
            availableTags={productMetaPage?.tags}
            orderSheets={productMetaPage?.orderSheets}
          />
        )}

        {isAdminOrderModalOpen && (
          <AdminOrderModal
            isOpen={isAdminOrderModalOpen}
            onClose={() => setIsAdminOrderModalOpen(false)}
            stores={modalStores}
            products={modalProducts}
            orderSheets={modalOrderSheets}
            initialCustomerName={adminOrderInitialCustomer.name}
            initialCustomerPhone={adminOrderInitialCustomer.phone}
            initialStoreId={adminOrderInitialStoreId}
            initialOrderSheetId={adminOrderInitialOrderSheetId}
            initialOrder={editingOrder}
            onRefresh={activeTab === 'ORDERS' ? undefined : refreshAfterOrderChange}
            onOrderMutated={handleOrderMutated}
            onOrderDeleted={handleOrderDeleted}
            initialUserId={adminOrderInitialCustomer.userId}
          />
        )}

        {historyCustomer && (
          <CustomerOrderHistoryModal
            isOpen={!!historyCustomer}
            onClose={() => setHistoryCustomer(null)}
            customerName={historyCustomer.name}
            customerPhone={historyCustomer.phone}
            userId={historyCustomer.userId}
            orderSheetId={historyCustomer.orderSheetId}
            onUpdateOrderStatus={handleUpdateOrderStatus}
            onUpdateOrderStatusBatch={handleUpdateOrderStatusBatch}
            onOpenAdminOrder={(order) => handleOpenAdminOrder('', '', order)}
            onOrderMutated={handleOrderMutated}
          />
        )}
      </div>
    </div>
  );
};
